import request from '@flowio/request';
import session from '@flowio/session';
import optionsSuccess from '@flowio/request/lib/success';
import optionsError from '@flowio/request/lib/error';
import ChannelEventEmitter from '@flowio/events/lib/channel-event-emitter';
import { SessionCache } from '@flowio/cache';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import head from 'lodash/head';
import tail from 'lodash/tail';
import pickBy from 'lodash/pickBy';
import isNil from 'lodash/isNil';
import inRange from 'lodash/inRange';
import reduce from 'lodash/reduce';
import isEqual from 'lodash/isEqual';

import constants from '../constants';
import { memo } from '../utilities/memo';

const { filterFlowProperties, invalidateDomCartCache } = require('./utilities');

const defaultEmitterSettings = { channel: 'shopify_cart' };

class ShopifyCart {
  /**
   * @constructor
   * @param {object} [data={}]
   * @param {boolean} [data.debug=false]
   * @param {string} data.flowApiBaseURI
   * @param {object} [data.emitterSettings]
   */
  constructor({ debug = false, flowApiBaseURI, emitterSettings = {} }) {
    this.debug = debug;
    const { channel } = ShopifyCart.getEmitterSettings(emitterSettings);
    this.emitter = new ChannelEventEmitter(channel);
    this.flowApiBaseURI = flowApiBaseURI;
    this.cache = new SessionCache({ storageKey: 'flow_cart_cache' });
    this.organization = session.getOrganization();

    this.emitter.on('updated', ({ cart }) => {
      this.log('event:updated: setting Cart data', cart);
      this.cartData = cart;
    });

    this.makeConversionRequest = memo((url, cart) => new Promise((resolve, reject) => {
      request.authenticated(
        {
          url,
          method: 'POST',
          data: cart,
          envelope: true,
        },
        (status, response) => {
          if (inRange(200, 300)) {
            resolve({
              status,
              response,
            });
          } else {
            reject({
              status,
              response,
            });
          }
        },
      );
    }));
  }

  static pickByIsNotNil(key, value) {
    return !isNil(value);
  }

  static getEmitterSettings(providedSettings = {}) {
    return {
      ...defaultEmitterSettings,
      ...providedSettings,
    };
  }

  log(message, ...params) {
    if (this.debug && console && console.log) { // eslint-disable-line no-console
      console.log(`[ShopifyCart] ${message}`, ...params); // eslint-disable-line no-console
    }
  }

  /**
   * TODO: Ideally get locally cached FLOW cart and update the attributes of the cart to have
   * localized subtotal.
   *
   * OR should this be called `setAttributes` or something similar?
   */
  syncCart(options = {}) {
    this.getCart({
      ...options,
      onSuccess: (cart) => {
        optionsSuccess(options, cart);
      },
    });
  }

  handleResponse(options, status, response, errorMessagePartial) {
    this.log('response', `status[${status}]`, response);
    if (inRange(status, 200, 300)) {
      optionsSuccess(options, response);
    } else {
      const {
        error: {
          message,
          description,
        } = {},
      } = response;

      let responseText;

      if (message) {
        responseText = `${message}: ${description}`;
      } else {
        responseText = JSON.stringify(response);
      }

      optionsError(options, `${errorMessagePartial} status[${status}] response[${responseText}]`);
    }
  }

  emitIfSuccessful(status, event, data) {
    if (inRange(status, 200, 300)) {
      this.emitter.trigger(event, data);
      this.emitter.trigger('message', { ...data, event });
    }
  }

  getItemLinePosition({ variantId, properties }) {
    const defaultPosition = -1;
    const filteredProperties = filterFlowProperties(properties);
    const exactMatch = reduce(get(this, 'cartData.items', []), (position, item, index) => {
      if (position === defaultPosition && variantId === item.variant_id &&
          isEqual(filteredProperties, filterFlowProperties(item.properties))) {
        return index + 1;
      }
      return position;
    }, defaultPosition);

    if (exactMatch === defaultPosition) {
      const fuzzyMatch = findIndex(get(this, 'cartData.items', []), { variant_id: variantId });
      if (fuzzyMatch !== defaultPosition) {
        return fuzzyMatch + 1;
      }
      return defaultPosition;
    }

    return exactMatch;
  }

  addItem(options = {}) {
    const { id, quantity, properties, addItems = false } = options;
    this.log('request', 'POST /cart/add.js', { id, quantity, properties });
    request.anonymous({
      url: '/cart/add.js',
      method: 'POST',
      data: pickBy({
        id,
        quantity,
        properties,
      }, ShopifyCart.pickByIsNotNil),
    }, (status, response) => {
      this.syncCart({
        onSuccess: (cart) => {
          this.emitIfSuccessful(status, 'addItem', { item: response, addItems, cart });
          this.emitIfSuccessful(status, 'updated', { cart });
          this.handleResponse(options, status, response, `Failed to add item. id[${id}] quantity[${quantity}]`);
        },
      });
    });
  }

  addItems(options = {}) {
    const items = get(options, 'items', []);
    const updatedItems = get(options, 'updatedItems', []);
    const currentItem = head(items);
    const remainingItems = tail(items);

    if (currentItem) {
      const { id, quantity } = currentItem;
      this.addItem({
        id,
        quantity,
        addItems: true,
        onSuccess: (item) => {
          if (remainingItems.length) {
            this.addItems({
              ...options,
              items: remainingItems,
              updatedItems: [...updatedItems, item],
            });
          } else {
            this.emitIfSuccessful(status, 'addItems', { items: [...updatedItems, item] });
            optionsSuccess(options, { items: [...updatedItems, item] });
          }
        },
        onError: error => optionsError(options, error.message),
      });
    }
  }

  updateItem(options = {}) {
    const { id, line, quantity, properties, removeItem = false } = options;
    const position = line || this.getItemLinePosition({ variantId: id, properties });
    let url = '/cart/update.js';

    const data = {
      quantity,
      properties,
    };

    if (position > -1) {
      url = '/cart/change.js';
      data.line = position; // TODO: ideally just use 'line' instead of position
    } else {
      data.id = id;
    }

    this.log('request', `POST ${url}`, { id, quantity, properties, position });

    request.anonymous({
      url,
      method: 'POST',
      data: pickBy(data, ShopifyCart.pickByIsNotNil),
    }, (status, response) => {
      this.emitIfSuccessful(status, 'updateItem', { cart: response, removeItem });
      this.emitIfSuccessful(status, 'updated', { cart: response });
      this.handleResponse(options, status, response, `Failed to update item. id[${id}] quantity[${quantity}]`);
    });
  }

  /**
   * @description Get Cart from the Dom if it is present, and has not previously been consumed.
   * We only consume once as the assumption is this will take place on page load or Dom Ready,
   * after then it's not safe to consider this data still relevant and not stale.
   * @return {object} Shopify Cart
   */
  static getCartFromDom() {
    const cartJsonEl = document.getElementById(constants.CART_JSON_CONTAINER_ID);
    const shouldUseDomElement = cartJsonEl
      && !cartJsonEl.hasAttribute(constants.CART_CONSUMED_ATTR_NAME);
    let cartJson;
    if (shouldUseDomElement) {
      try {
        cartJson = JSON.parse(cartJsonEl.innerText);
      } catch (e) {
        // doing nothing deliberately :-O
      }
      invalidateDomCartCache();
    }
    return cartJson;
  }

  getCart(options = {}) {
    this.log('request', 'GET /cart.js');
    const domCart = ShopifyCart.getCartFromDom();
    if (domCart) {
      optionsSuccess(options, domCart);
      return;
    }
    request.anonymous({
      url: '/cart.js',
    }, (status, response) => {
      this.emitIfSuccessful(status, 'getCart', { cart: response });
      this.emitIfSuccessful(status, 'updated', { cart: response });
      this.handleResponse(options, status, response, 'Failed to get cart.');
    });
  }

  getCachedLocalizedCart(cartKey, cachingEnabled) {
    if (cachingEnabled) {
      return this.cache.get(cartKey);
    }
    return undefined;
  }

  /**
   * @description get the cache key for this cart
   * @param {object} cart - cart JavaScript object
   * @param {string} currency - currency ISO 3 character string
   * @param {string} experience - experience key
   */
  static getCartCacheKey(cart, currency, experience) {
    const cartCacheKeyObject = Object.assign({}, cart);
    // insert key with random uniqueness to avoid accidental conflict.
    cartCacheKeyObject.flowLocaleHash_2387162381723 = currency + experience;
    return cartCacheKeyObject;
  }

  getFlowOrderFromCart(cart, opts = {}) {
    const options = {
      cache: 'enabled',
      ...opts,
    };
    const organization = session.getOrganization();
    const experience = session.getExperience();
    const currency = session.getCurrency();
    const url = `${this.flowApiBaseURI}/${organization}/experiences/${experience}/shopify/cart/conversions`;
    const cachingEnabled = get(options, 'cache') === 'enabled';
    const cachedLocalizedCart = this.getCachedLocalizedCart(
      ShopifyCart.getCartCacheKey(cart, currency, experience),
      cachingEnabled,
    );

    if (!isNil(cachedLocalizedCart)) {
      this.handleResponse(options, 200, cachedLocalizedCart);
      return;
    }

    if (isNil(experience)) {
      this.handleResponse(options, 500, {
        message: 'Experience not defined',
      });
      return;
    }

    this.makeConversionRequest(url, cart).then(({
      status,
      response,
    }) => {
      if (cachingEnabled) {
        this.cache.set(ShopifyCart.getCartCacheKey(cart, currency, experience), response);
      }

      this.handleResponse(options, status, response, 'Failed to build order.');
    })
    .catch(({
      status,
      err,
    }) => this.handleResponse(options, status, err, 'Failed to build order.'));
  }

  removeItem(options = {}) {
    this.updateItem({
      ...options,
      quantity: 0,
      removeItem: true,
      onSuccess: (cart) => {
        this.emitIfSuccessful(status, 'removeItem', { cart });
        this.emitIfSuccessful(status, 'updated', { cart });
        optionsSuccess(options, cart);
      },
    });
  }

  setAttributes(options = {}) {
    const { attributes, replace = false } = options;
    const cartDataAttributes = get(this.cartData, 'attributes', {});
    let combinedAttributes = {
      ...cartDataAttributes,
      ...attributes,
    };

    if (replace) {
      combinedAttributes = attributes;
    }

    this.log('request', 'PUT /cart/update.js', { attributes, combinedAttributes, replace });
    request.anonymous({
      url: '/cart/update.js',
      method: 'POST',
      data: { attributes: combinedAttributes },
    }, (status, response) => {
      this.emitIfSuccessful(status, 'setAttributes', { attributes: combinedAttributes, cart: response });
      this.emitIfSuccessful(status, 'updated', { cart: response });
      this.handleResponse(options, status, response, 'Failed to update cart attributes.');
    });
  }

  clear(options = {}) {
    this.log('request', 'POST /cart/clear.js');
    request.anonymous({
      url: '/cart/clear.js',
      method: 'POST',
    }, (status, response) => {
      this.emitIfSuccessful(status, 'clear', { cart: response });
      this.emitIfSuccessful(status, 'updated', { cart: response });
      this.handleResponse(options, status, response, 'Failed to clear cart.');
    });
  }
}

module.exports = ShopifyCart;



// WEBPACK FOOTER //
// ./static/js/v0/cart_unified/ShopifyCart.js